// src/Helpers/Header.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import './Header.css';
import { isCapacitor, isAndroid, isIOS, isElectron } from '../../utils/platform';
import { useAuth } from '../../context/AuthContext';
import { useRecorderPanel } from '../../context/RecorderPanelContext';
import Button from '../buttons/Button';
import HoverTooltip from '../tooltips/HoverTooltip';
import { createLogger } from '../../services/Debug';
import {
  FaFileAlt,
  FaHome,
  FaMicrophone,
  FaTimes,
  FaWindowMinimize,
  FaRecordVinyl
} from 'react-icons/fa';

function Header({ handleLogout }) {
  const { isSubscribed, profile } = useAuth();
  const { isOpen: isRecorderPanelOpen, panelMode, togglePanel, isWebOnly } = useRecorderPanel();
  const log = createLogger('Header');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isRecorderVisible, setIsRecorderVisible] = useState(false);
  // Live window visibility tracking (independent window)
  const [isLiveVisible, setIsLiveVisible] = useState(false);
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState(false);
  const [shortcuts, setShortcuts] = useState({});
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const location = useLocation();

  // Check if we're on pages that support side panel integration
  const isOnReportsPage = location.pathname.startsWith('/reports');
  const isOnHomePage = location.pathname.startsWith('/home');
  const supportsSidePanel = isOnReportsPage || isOnHomePage;

  // Check if user has Gold or Enterprise subscription
  const isGoldOrEnterprise = useMemo(() => {
    return (
      profile?.isGoldOrEnterprise === true ||
      profile?.isgoldorenterprise === true ||
      profile?.subscription?.planType === 'gold' ||
      profile?.subscription?.planType === 'enterprise' ||
      profile?.subscription?.planType === 'Gold' ||
      profile?.subscription?.planType === 'Enterprise'
    );
  }, [profile]);

  useEffect(() => {
    loadUserData();
    
    // Listen for profile updates
    const handleStorageChange = (e) => {
      if (e.key === 'userProfile' || e.key === 'isEnterpriseAdmin' || e.key === 'isAdmin') {
        loadUserData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for custom events that might signal profile updates
    const handleProfileUpdate = () => {
      loadUserData();
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  // Determine if we should render the mobile bottom bar variant on web (non-Electron) based on viewport
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const update = () => setIsMobileViewport(mediaQuery.matches);
    update();
    try {
      // Modern browsers
      mediaQuery.addEventListener('change', update);
    } catch (_) {
      // Safari fallback
      mediaQuery.addListener(update);
    }
    return () => {
      try {
        mediaQuery.removeEventListener('change', update);
      } catch (_) {
        mediaQuery.removeListener(update);
      }
    };
  }, []);

  // Load current Electron shortcuts (for tooltip display)
  useEffect(() => {
    (async () => {
      try {
        if (isElectron && window.electronAPI?.getShortcuts) {
          const map = await window.electronAPI.getShortcuts();
          setShortcuts(map || {});
        }
      } catch (_) {}
    })();
  }, []);

  // Track recorder window visibility (Electron only)
  useEffect(() => {
    if (!isElectron || !window.electronAPI) return;
    let unsubscribe = null;
    try {
      unsubscribe = window.electronAPI.on('recorder:visibility', (_e, visible) => {
        setIsRecorderVisible(!!visible);
      });
    } catch (_) {}
    // Fetch initial visibility
    (async () => {
      try {
        const visible = await window.electronAPI.invoke('recorder:isVisible');
        setIsRecorderVisible(!!visible);
      } catch (_) {}
    })();
    return () => { if (unsubscribe) try { unsubscribe(); } catch (_) {} };
  }, []);

  // Track live window visibility from main process
  useEffect(() => {
    if (!isElectron || !window.electronAPI?.on) return;
    let remove = null;
    try {
      remove = window.electronAPI.on('liveRecorder:visibility', (_e, visible) => {
        setIsLiveVisible(!!visible);
      });
    } catch (_) {}
    // Fetch initial visibility
    (async () => {
      try {
        const visible = await window.electronAPI.invoke('liveRecorder:isVisible');
        setIsLiveVisible(!!visible);
      } catch (_) {}
    })();
    return () => { if (remove) try { remove(); } catch (_) {} };
  }, []);

  const loadUserData = () => {
    try {
      
      if (profile) {
        
        
        // Set user name
        if (profile.fullName) {
          setUserName((profile.title ? profile.title + '. ' : '') + profile.fullName);
        }
        
        // Check for system admin (general admin)
        const isSystemAdmin = profile.isAdmin || profile.user?.isAdmin || false;
        setIsAdmin(isSystemAdmin);
        
        // Check for enterprise admin
        const isEntAdmin = profile.isEnterpriseAdmin || false;
        setIsEnterpriseAdmin(isEntAdmin);
        
      }
    } catch (error) {
      log.error('Error loading user data in Header:', error);
      
      // Final fallback to localStorage
      const storedName = localStorage.getItem('fullName');
      if (storedName) {
        setUserName(storedName);
      }
      
      const storedIsAdmin = localStorage.getItem('isAdmin') === 'true';
      setIsAdmin(storedIsAdmin);
      
      const storedIsEnterpriseAdmin = localStorage.getItem('isEnterpriseAdmin') === 'true';
      setIsEnterpriseAdmin(storedIsEnterpriseAdmin);
    }
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const closeNav = () => {
    setIsNavOpen(false);
  };

  // Removed Capacitor top bar label logic (mobileTopBarLabel) since the top bar is no longer rendered on Capacitor

  // Tooltip texts (fallback to defaults if not configured)
  const dictaphoneShortcut = shortcuts?.toggleShowRecorder?.key || '';
  const macrosShortcut = shortcuts?.openMacroSearch?.key || 'Alt+Space';
  const dictaphoneTooltipText = dictaphoneShortcut ? 'Raccourci: ' + dictaphoneShortcut : '';
  const macrosTooltipText = 'Raccourci: ' + macrosShortcut;
  const liveShortcut = shortcuts?.toggleLiveRecord?.key || '';
  const liveTooltipText = liveShortcut ? ('Raccourci: ' + liveShortcut) : 'Live';
  const hideShortcut = shortcuts?.toggleApp?.key || '';
  const hideTooltipText = hideShortcut ? `${hideShortcut}` : 'Minimiser';

  const isMobileBottomBar = isCapacitor || (!isElectron && isMobileViewport);

  // If user is not subscribed, show only a logout button
  if (!isSubscribed) {
    return (
      <>
        <header
          className={`app-header ${isMobileBottomBar ? 'mobile' : ''} ${isIOS ? 'ios' : ''} ${isAndroid ? 'android gradient-primary' : ''} ${isElectron ? 'electron' : ''}`}
        >
          <div className="header-right" style={{ marginLeft: 'auto' }}>
            <NavLink
              to="#logout"
              className="nav-link nav-main nav-logout"
              onClick={(e) => { e.preventDefault(); handleLogout(); }}
            >
              <span className="link-text">Se déconnecter</span>
            </NavLink>
          </div>
        </header>
      </>
    );
  }

  return (
    <>


      <header
        className={`app-header ${isMobileBottomBar ? 'mobile' : ''} ${isIOS ? 'ios' : ''} ${isAndroid ? 'android gradient-primary' : ''} ${isElectron ? 'electron' : ''} ${!isElectron && isGoldOrEnterprise ? 'has-live-dictee' : ''}`}
      >
        {/* Left: Electron controls + (non-electron) Logo/Title */}
        <div className="header-left">
          {/* Electron controls on extreme left */}
          {!isCapacitor && isElectron && (
            <>
              {/* Hide app (Electron only) */}
              <button
                  className="header-hide-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.electronAPI?.quitApp?.(); } catch (e2) {}
                  }}
                  title="Quitter"
                  aria-label="Quitter"
                >
                  <FaTimes className="hide-btn-icon" />
                </button>


              <HoverTooltip text={hideTooltipText} position="bottom">
                <button
                  className="header-hide-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.electronAPI?.invoke('app:minimize'); } catch (e2) {}
                  }}
                  title="Minimiser"
                  aria-label="Minimiser"
                >
                  <FaWindowMinimize className="hide-btn-icon" />
                </button>
              </HoverTooltip>

              <HoverTooltip
                text={liveTooltipText}
                position="bottom"
              >
                <NavLink
                  to="#live"
                  className={() => `nav-link nav-main ${isLiveVisible ? 'active' : ''}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      setIsLiveVisible(true);
                      // Toggle independent Live window
                      await window.electronAPI.invoke('liveRecorder:toggle');
                    } catch (e2) {
                      log.error('Failed to toggle live recorder:', e2);
                    }
                    closeNav();
                  }}
                >
                  <FaRecordVinyl />
                  <span className="link-text">Dictée Live</span>
                </NavLink>
              </HoverTooltip>
            </>
          )}
          {!isElectron && (
            <>
              <NavLink to="/home">
                <img src="logo.png" alt="Logo" className="logo" />
              </NavLink>
              <h1 className="header-title">voxia</h1>
            </>
          )}
        </div>

        {/* Center: Electron-only centered logo */}
        {!isCapacitor && isElectron && (
          <div className="header-center">
            <NavLink to="/home">
              <img src="logo.png" alt="Logo" className="logo" />
            </NavLink>
          </div>
        )}

        {/* Right side: Navigation links */}
        <div className="header-right">
          {isMobileBottomBar ? (
            <>
              {/* Rapports */}
              <NavLink
                to="/reports"
                className={({ isActive }) => `nav-link nav-main nav-rapports${isActive ? ' active' : ''}`}
                onClick={closeNav}
              >
                <FaFileAlt />
              </NavLink>

              {/* Dictaphone */}
              {isWebOnly && supportsSidePanel ? (
                <button
                  className={`nav-link nav-main nav-dictaphone${isRecorderPanelOpen ? ' active' : ''}`}
                  onClick={() => { togglePanel('recorder'); closeNav(); }}
                >
                  <FaMicrophone />
                </button>
              ) : (
                <NavLink
                  to="/recorder"
                  className={({ isActive }) => `nav-link nav-main nav-dictaphone${isActive ? ' active' : ''}`}
                  onClick={closeNav}
                >
                  <FaMicrophone />
                </NavLink>
              )}

              {/* Accueil */}
              <NavLink
                to="/home"
                className={({ isActive }) => `nav-link nav-main nav-home${isActive ? ' active' : ''}`}
                onClick={closeNav}
              >
                <FaHome />
              </NavLink>
            </>
          ) : (
            <>
              {/* Rapports */}
              <NavLink
                to="/reports"
                className={({ isActive }) => `nav-link nav-main nav-rapports${isActive ? ' active' : ''}`}
                onClick={closeNav}
              >
                <FaFileAlt />
                <span className="link-text">Rapports</span>
              </NavLink>

              {/* Dictaphone - Electron version */}
              {isElectron ? (
                <HoverTooltip text={dictaphoneTooltipText} position="bottom">
                  <NavLink
                    to="#recorder"
                    className={() => `nav-link nav-main nav-dictaphone ${isRecorderVisible && !isLiveVisible ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      try {
                        // Optimistically update header state for correct highlight
                        setIsLiveVisible(false);
                        // Toggle behavior: if already visible, hide; otherwise show
                        setIsRecorderVisible((prev) => !prev);
                        (async () => {
                          try {
                            // Always ensure normal mode before toggling
                            await window.electronAPI.invoke('recorder:mode', { live: false });
                            // Toggle visibility (show if closed, hide if open)
                            await window.electronAPI.invoke('recorder:toggle');
                          } catch (e2) {
                            log.error('Failed to open recorder window:', e2);
                          }
                        })();
                      } catch (e2) {
                        log.error('Failed to open recorder window:', e2);
                      }
                      closeNav();
                    }}
                  >
                    <FaMicrophone />
                    <span className="link-text">Dictaphone</span>
                  </NavLink>
                </HoverTooltip>
              ) : (
                /* Dictaphone - Web version: side panel on supported pages, NavLink elsewhere */
                isWebOnly && supportsSidePanel ? (
                  <button
                    className={`nav-link nav-main nav-dictaphone${isRecorderPanelOpen ? ' active' : ''}`}
                    onClick={() => { togglePanel('recorder'); closeNav(); }}
                  >
                    <FaMicrophone />
                    <span className="link-text">Dictaphone</span>
                  </button>
                ) : (
                  <NavLink
                    to="/recorder"
                    className={({ isActive }) => `nav-link nav-main nav-dictaphone${isActive ? ' active' : ''}`}
                    onClick={closeNav}
                  >
                    <FaMicrophone />
                    <span className="link-text">Dictaphone</span>
                  </NavLink>
                )
              )}

              {/* Accueil */}
              <NavLink
                to="/home"
                className={({ isActive }) => `nav-link nav-main nav-home${isActive ? ' active' : ''}`}
                onClick={closeNav}
              >
                <FaHome />
                <span className="link-text">Accueil</span>
              </NavLink>
            </>
          )}
          {/* Burger menu and dropdown removed as navigation is simplified */}
        </div>
      </header>

    </>
  );
}

export default Header;